import { Component, ReactNode, ErrorInfo } from "react";

interface ErrorBoundaryState {
    hasError: boolean;
    error: Error | null;
    errorInfo: ErrorInfo | null;
}

interface ErrorBoundaryProps {
    children: ReactNode;
    fallback?: (error: Error | null, retry: () => void) => ReactNode;
    onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null,
        };
    }

    static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
        console.log("🚨 ErrorBoundary caught an error!", error, errorInfo);

        // Store error info for debugging
        this.setState({ errorInfo });

        console.error("Error caught by ErrorBoundary:", error, errorInfo);

        // Handle chunk loading and dynamic import errors
        this.handleLoadingErrors(error);

        // Optional: Report to error tracking service
        if (this.props.onError) {
            this.props.onError(error, errorInfo);
        }
    }

    private handleLoadingErrors = (error: Error): void => {
        const isLoadingError = this.isLoadingError(error);

        console.log("🔍 ErrorBoundary checking error:", {
            message: error.message,
            name: error.name,
            isLoadingError,
            stack: error.stack,
        });

        if (isLoadingError) {
            console.log(
                "✅ ErrorBoundary caught a loading/dynamic import error:",
                error,
            );

            const hasReloaded = sessionStorage.getItem(
                "hasReloadedOnLoadingError",
            );
            const reloadCount = parseInt(
                sessionStorage.getItem("loadingErrorReloadCount") || "0",
            );

            // Prevent infinite reload loops - max 2 attempts
            if (!hasReloaded && reloadCount < 2) {
                sessionStorage.setItem("hasReloadedOnLoadingError", "true");
                sessionStorage.setItem(
                    "loadingErrorReloadCount",
                    (reloadCount + 1).toString(),
                );

                // console.log("🔄 Force reloading due to loading error:", error.message);

                // Clear cache and force reload
                this.clearCacheAndReload();
            } else {
                console.warn(
                    "⚠️ Max reload attempts reached for loading error",
                );
            }
        } else {
            console.log(
                "❌ ErrorBoundary determined this is NOT a loading error:",
                error.message,
            );
        }
    };

    private isLoadingError = (error: Error): boolean => {
        const errorMessage = error?.message?.toLowerCase() || "";
        const errorName = error?.name?.toLowerCase() || "";

        // Check for various types of loading errors
        return (
            // Chunk loading errors
            errorMessage.includes("loading chunk") ||
            errorMessage.includes("loading css chunk") ||
            errorName === "chunkloaderror" ||
            // Dynamic import errors
            errorMessage.includes("failed to import") ||
            errorMessage.includes(
                "error loading dynamically imported module",
            ) ||
            errorMessage.includes("loading module") ||
            errorMessage.includes("import() failed") ||
            errorMessage.includes("dynamic import") ||
            // Network/fetch errors related to modules
            (errorMessage.includes("fetch") &&
                errorMessage.includes("module")) ||
            errorMessage.includes("networkerror") ||
            // Module not found errors
            (error as any)?.code === "MODULE_NOT_FOUND" ||
            errorName === "modulenotfounderror" ||
            // Script loading errors
            errorMessage.includes("script error") ||
            errorMessage.includes("loading script") ||
            // Webpack specific errors
            (errorMessage.includes("webpack") &&
                errorMessage.includes("load")) ||
            // 404 errors for JS/CSS files
            (errorMessage.includes("404") &&
                (errorMessage.includes(".js") || errorMessage.includes(".css")))
        );
    };

    private clearCacheAndReload = (): void => {
        // Clear browser caches if available
        if ("caches" in window) {
            caches
                .keys()
                .then((names: string[]) => {
                    const deletePromises = names.map((name: string) =>
                        caches.delete(name),
                    );
                    return Promise.all(deletePromises);
                })
                .then(() => {
                    console.log("🧹 Browser caches cleared");
                })
                .catch((err) => {
                    console.warn("⚠️ Error clearing caches:", err);
                })
                .finally(() => {
                    this.forceReload();
                });
        } else {
            this.forceReload();
        }
    };

    private forceReload = (): void => {
        // Force reload with cache bypass
        if (window.location) {
            // Add timestamp to force cache bypass
            const url = new URL(window.location.href);
            url.searchParams.set("_reload", Date.now().toString());
            window.location.href = url.toString();
        } else {
            // Fallback
            (window as any).location.href = (window as any).location.href;
        }
    };

    componentDidMount() {
        // Clear reload flags after successful mount
        const clearFlags = (): void => {
            sessionStorage.removeItem("hasReloadedOnLoadingError");
            sessionStorage.removeItem("loadingErrorReloadCount");
            // Clean up old flag names for backward compatibility
            sessionStorage.removeItem("hasReloadedOnChunkError");
            sessionStorage.removeItem("chunkErrorReloadCount");
        };

        // Clear flags after a delay to ensure app is stable
        setTimeout(clearFlags, 10000);

        // Also listen for unhandled promise rejections that might be dynamic import failures
        const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
            const error = event.reason;
            console.log("🔍 Unhandled promise rejection detected:", error);

            if (error instanceof Error && this.isLoadingError(error)) {
                console.log(
                    "✅ Unhandled rejection is a loading/dynamic import error:",
                    error,
                );
                this.handleLoadingErrors(error);
                event.preventDefault(); // Prevent default error handling
            } else {
                console.log(
                    "❌ Unhandled rejection is NOT a loading error:",
                    error,
                );
            }
        };

        window.addEventListener("unhandledrejection", handleUnhandledRejection);

        // Clean up listener on unmount
        return () => {
            window.removeEventListener(
                "unhandledrejection",
                handleUnhandledRejection,
            );
        };
    }

    private retry = (): void => {
        this.setState({
            hasError: false,
            error: null,
            errorInfo: null,
        });
    };

    render(): ReactNode {
        if (this.state.hasError) {
            const isLoadingError = this.state.error
                ? this.isLoadingError(this.state.error)
                : false;

            // Custom error UI from props
            if (this.props.fallback) {
                return this.props.fallback(this.state.error, this.retry);
            }

            return (
                <div
                    className="error-boundary"
                    role="alert"
                    aria-live="assertive"
                    style={{
                        padding: "2rem",
                        textAlign: "center",
                        backgroundColor: "#f8f9fa",
                        border: "1px solid #dee2e6",
                        borderRadius: "0.5rem",
                        margin: "1rem",
                    }}
                >
                    <h1 style={{ color: "#dc3545", marginBottom: "1rem" }}>
                        {isLoadingError
                            ? "Loading Error"
                            : "Something went wrong"}
                    </h1>

                    <p style={{ marginBottom: "1.5rem", color: "#6c757d" }}>
                        {isLoadingError
                            ? "There was a problem loading the application resources. The page will reload automatically."
                            : this.state.error?.message ||
                              "An unexpected error occurred."}
                    </p>

                    <div>
                        <button
                            onClick={this.retry}
                            style={{
                                padding: "0.5rem 1rem",
                                backgroundColor: "#007bff",
                                color: "white",
                                border: "none",
                                borderRadius: "0.25rem",
                                cursor: "pointer",
                                marginRight: "0.5rem",
                            }}
                        >
                            Try Again
                        </button>

                        <button
                            onClick={() => this.clearCacheAndReload()}
                            style={{
                                padding: "0.5rem 1rem",
                                backgroundColor: "#6c757d",
                                color: "white",
                                border: "none",
                                borderRadius: "0.25rem",
                                cursor: "pointer",
                            }}
                        >
                            Force Refresh
                        </button>
                    </div>

                    {/* Show error details in development */}
                    {process.env.NODE_ENV === "development" && (
                        <details
                            style={{ marginTop: "2rem", textAlign: "left" }}
                        >
                            <summary
                                style={{
                                    cursor: "pointer",
                                    marginBottom: "1rem",
                                }}
                            >
                                Error Details (Development Only)
                            </summary>
                            <pre
                                style={{
                                    backgroundColor: "#f1f3f4",
                                    padding: "1rem",
                                    borderRadius: "0.25rem",
                                    overflow: "auto",
                                    fontSize: "0.875rem",
                                }}
                            >
                                {this.state.error?.stack}
                                {this.state.errorInfo &&
                                    "\n\nComponent Stack:" +
                                        this.state.errorInfo.componentStack}
                            </pre>
                        </details>
                    )}
                </div>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;

// Optional: Export types for use in other components
export type { ErrorBoundaryProps, ErrorBoundaryState };
